import { useEffect, useRef } from "react";
import {
  META_PIXEL_ID,
  POSTHOG_KEY,
  capturePageview,
  captureUtm,
  emitDefaultConsent,
  loadMetaPixel,
  loadPostHog,
  shouldLoadPostHog,
  posthogOptIn,
  posthogOptOut,
  setClusterTag,
  updateConsent,
} from "../lib/analytics";
import { useConsent } from "../lib/consent/store";

/**
 * Wires vendor SDKs behind the in-house CMP (LL-0024).
 *
 * GA4 is loaded via the inline <head> snippet in `index.html` with
 * Consent-Mode-v2 defaults pre-denied — it sits idle until the CMP fires
 * `gtag('consent','update',...)` via `updateConsent`.
 *
 * PostHog and Meta Pixel are NOT instantiated until the user grants the
 * appropriate consent category (analytics / marketing). No vendor SDK hits
 * the network pre-consent beyond Google's gtag (which is itself consent-
 * gated server-side by the default-deny signal).
 */
export default function AnalyticsScripts() {
  const { state } = useConsent();
  const didEmitDefault = useRef(false);
  const didLoadPostHog = useRef(false);
  const didLoadMetaPixel = useRef(false);
  const didCaptureInitialPageview = useRef(false);

  // Defensive: the head snippet should have emitted defaults already.
  // This is a no-op if window.gtag exists.
  if (!didEmitDefault.current && typeof window !== "undefined") {
    emitDefaultConsent();
    didEmitDefault.current = true;
  }

  useEffect(() => {
    if (!state) return;

    // 1. Flip GA4 Consent Mode v2 signals.
    updateConsent(state.categories);

    // 2. PostHog: init-on-consent (never pre-consent).
    if (shouldLoadPostHog(state.categories)) {
      if (!didLoadPostHog.current) {
        loadPostHog();
        didLoadPostHog.current = true;
      }
      posthogOptIn();

      // ClusterTagBinder fires $pageview on every pathname change, but its
      // useEffect on initial mount runs BEFORE consent is granted -- so the
      // landing page is never captured for external arrivals (share links
      // into /events/<slug>, paid traffic, etc.) who accept cookies AFTER
      // page load and then leave without navigating. Backfill the initial
      // $pageview here so the landing is recorded once consent flips on.
      if (!didCaptureInitialPageview.current && typeof window !== "undefined") {
        captureUtm();
        setClusterTag(null);
        capturePageview();
        didCaptureInitialPageview.current = true;
      }
    } else if (POSTHOG_KEY && didLoadPostHog.current) {
      posthogOptOut();
    }

    // 3. Meta Pixel: init-on-consent (marketing category only).
    if (
      state.categories.marketing &&
      META_PIXEL_ID &&
      !didLoadMetaPixel.current
    ) {
      loadMetaPixel(META_PIXEL_ID);
      didLoadMetaPixel.current = true;
    }
  }, [state]);

  return null;
}
